import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { Stack, SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { ReactElement, ReactNode } from 'react';

import { ViewWrapperNewContextProvider } from '@/components/viewWrapperNew/context/ViewWrapperNewContextProvider.util.tsx';
import { ViewWrapperNewPaperStyles } from '@/components/viewWrapperNew/viewWrapperNew.styles.ts';
import { themeColors } from '@/config/theme/themeColors.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

type Props<ITEM_TYPE extends object> = {
  id?: string;
  isError?: boolean;
  isLoading?: boolean;
  spacing?: number;
  sx?: SxProps<Theme>;
  withSpacing?: boolean;
} & (
  | {
      children: (item: Required<ITEM_TYPE>) => ReactNode;
      item?: ITEM_TYPE;
      withItem: true;
    }
  | {
      children: ReactNode;
      item?: null;
      withItem?: false;
    }
);

export const ViewWrapperNew = <ITEM_TYPE extends object>(
  props: Props<ITEM_TYPE>,
): ReactElement<Props<ITEM_TYPE>> => {
  const { t: tError } = useTranslationWithPrefix('utils.errorMessages');
  const { children, id, isError, isLoading, item, spacing, sx, withItem, withSpacing } = props;

  const mainStyles = { ...ViewWrapperNewPaperStyles, ...(sx ?? {}) };

  if (isError) {
    return (
      <Paper id={id} square sx={mainStyles}>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            height: '100%',
            justifyContent: 'center',
          }}
        >
          <Stack
            direction="row"
            spacing={3}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <ReportProblemOutlinedIcon sx={{ color: themeColors.red, fontSize: '48px' }} />
            <Typography variant="f16Medium">{tError('unknownError')}</Typography>
            <ReportProblemOutlinedIcon sx={{ color: themeColors.red, fontSize: '48px' }} />
          </Stack>
        </Box>
      </Paper>
    );
  }

  if (isLoading || (withItem && item === undefined)) {
    return (
      <Paper id={id} square sx={mainStyles}>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            height: '100%',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={64} sx={{ color: themeColors.accentColor }} />
        </Box>
      </Paper>
    );
  }

  if (withItem && item !== undefined) {
    return (
      <ViewWrapperNewContextProvider contextObject={item}>
        <Stack id={id} spacing={withSpacing ? 4 : spacing} sx={mainStyles}>
          {withItem ? children(item as Required<ITEM_TYPE>) : children}
        </Stack>
      </ViewWrapperNewContextProvider>
    );
  }

  return (
    <Stack id={id} spacing={withSpacing ? 4 : spacing} sx={mainStyles}>
      {withItem ? children(item as Required<ITEM_TYPE>) : children}
    </Stack>
  );
};
